import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useProvinces } from '../../../hooks/useProvinces';
import type { SchoolCreateRequest } from '../types';
import { SCHOOL_LEVELS } from '../constants';

interface CreateSchoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SchoolCreateRequest) => Promise<void>;
  isCreating: boolean;
}

const SCHOOL_TYPES = [
  { value: 'public', label: 'Trường Công lập' },
  { value: 'private', label: 'Trường Tư thục' },
  { value: 'international', label: 'Trường Quốc tế' },
  { value: 'vocational', label: 'Trường Nghề' },
];

const CreateSchoolModal: React.FC<CreateSchoolModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isCreating,
}) => {
  const [formData, setFormData] = useState<SchoolCreateRequest>({
    name: '',
    code: '',
    type: '',
    level: '',
    address: '',
    district: '',
    city: '',
    districtCode: undefined,
    cityCode: undefined,
    phone: '',
    email: '',
    website: '',
    principal: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<number | null>(null);
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<number | null>(null);

  // Use provinces hook
  const {
    provinces,
    districts,
    isLoadingProvinces,
    isLoadingDistricts,
    error: provincesError,
    loadDistrictsByProvince,
    getProvinceByCode,
    getDistrictByCode,
    clearDistricts,
  } = useProvinces();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tên trường học là bắt buộc';
    }

    // Only validate email and phone format if they are provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email không hợp lệ';
    }

    if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Số điện thoại không hợp lệ';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      handleReset();
      onClose();
    } catch (error) {
      // Error handling được xử lý ở component cha
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      code: '',
      type: '',
      level: '',
      address: '',
      district: '',
      city: '',
      districtCode: undefined,
      cityCode: undefined,
      phone: '',
      email: '',
      website: '',
      principal: '',
    });
    setErrors({});
    setSelectedProvinceCode(null);
    setSelectedDistrictCode(null);
    clearDistricts();
  };

  const handleInputChange = (field: keyof SchoolCreateRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle province selection
  const handleProvinceChange = (provinceCode: number) => {
    setSelectedProvinceCode(provinceCode);
    setSelectedDistrictCode(null);
    clearDistricts();
    
    const province = getProvinceByCode(provinceCode);
    if (province) {
      setFormData(prev => ({ 
        ...prev, 
        city: province.name,
        cityCode: province.code,
        district: '',
        districtCode: undefined
      }));
      loadDistrictsByProvince(provinceCode);
    }
  };

  // Handle district selection
  const handleDistrictChange = (districtCode: number) => {
    setSelectedDistrictCode(districtCode);
    
    const district = getDistrictByCode(districtCode);
    if (district) {
      setFormData(prev => ({ 
        ...prev, 
        district: district.name,
        districtCode: district.code
      }));
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      handleReset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
        <div className="flex flex-col">
          {/* Header với background */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Tạo trường học mới
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Nhập thông tin chi tiết để tạo trường học mới trong hệ thống
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Form content */}
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tên trường học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Tên trường học *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Nhập tên trường học"
                disabled={isCreating}
                className={`h-10 ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Mã trường học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Mã trường học
              </label>
              <Input
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                placeholder="Nhập mã trường học"
                disabled={isCreating}
                className={`h-10 ${errors.code ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {errors.code && (
                <p className="text-xs text-red-500 mt-1">{errors.code}</p>
              )}
            </div>

            {/* Loại trường học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Loại trường học
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                disabled={isCreating}
                className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                  errors.type ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Chọn loại trường học</option>
                {SCHOOL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p className="text-xs text-red-500 mt-1">{errors.type}</p>
              )}
            </div>

            {/* Cấp học */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Cấp học
              </label>
              <select
                value={formData.level}
                onChange={(e) => handleInputChange('level', e.target.value)}
                disabled={isCreating}
                className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                  errors.level ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Chọn cấp học</option>
                {SCHOOL_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              {errors.level && (
                <p className="text-xs text-red-500 mt-1">{errors.level}</p>
              )}
            </div>

                        {/* Địa chỉ */}
            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Địa chỉ
              </label>
              <Input
                value={typeof formData.address === 'string' ? formData.address : ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Nhập địa chỉ trường học"
                disabled={isCreating}
                className={`h-10 ${errors.address ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {errors.address && (
                <p className="text-xs text-red-500 mt-1">{errors.address}</p>
              )}
            </div>

            {/* Thành phố/Tỉnh */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Thành phố/Tỉnh
              </label>
              <select
                value={selectedProvinceCode || ''}
                onChange={(e) => handleProvinceChange(Number(e.target.value))}
                disabled={isCreating || isLoadingProvinces}
                className="h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 border-gray-300"
              >
                <option value="">
                  {isLoadingProvinces ? 'Đang tải...' : 'Chọn tỉnh/thành phố'}
                </option>
                {provinces.map((province) => (
                  <option key={province.code} value={province.code}>
                    {province.name}
                  </option>
                ))}
              </select>
              {provincesError && (
                <p className="text-xs text-red-500 mt-1">{provincesError}</p>
              )}
            </div>

            {/* Quận/Huyện */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Quận/Huyện
              </label>
              <select
                value={selectedDistrictCode || ''}
                onChange={(e) => handleDistrictChange(Number(e.target.value))}
                disabled={isCreating || isLoadingDistricts || !selectedProvinceCode}
                className="h-10 w-full rounded-md border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 border-gray-300"
              >
                <option value="">
                  {isLoadingDistricts ? 'Đang tải...' : !selectedProvinceCode ? 'Chọn tỉnh/thành phố trước' : 'Chọn quận/huyện'}
                </option>
                {districts.map((district) => (
                  <option key={district.code} value={district.code}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Số điện thoại */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Số điện thoại
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Nhập số điện thoại"
                disabled={isCreating}
                className={`h-10 ${errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Nhập email"
                disabled={isCreating}
                className={`h-10 ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Website */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Website
              </label>
              <Input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="Nhập website"
                disabled={isCreating}
                className="h-10 border-gray-300 focus:border-blue-500"
              />
            </div>

            {/* Hiệu trưởng */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Hiệu trưởng
              </label>
              <Input
                value={formData.principal}
                onChange={(e) => handleInputChange('principal', e.target.value)}
                placeholder="Nhập tên hiệu trưởng"
                disabled={isCreating}
                className={`h-10 ${errors.principal ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}`}
              />
              {errors.principal && (
                <p className="text-xs text-red-500 mt-1">{errors.principal}</p>
              )}
            </div>


          </div>

                        {/* Footer với actions */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isCreating}
                  className="px-6 py-2"
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  isLoading={isCreating}
                  disabled={isCreating}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700"
                >
                  {isCreating ? 'Đang tạo...' : 'Tạo trường học'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSchoolModal; 